import type { AxiosInstance } from 'axios';
/**
 * Agent work queue service.
 *
 * Fetches the four urgency buckets from GET /api/v1/agents/{agentId}/work-queue:
 *   · tasks            — pending agent_tasks that are overdue or due today
 *   · expiring_commitments — active commitments expiring within lookahead_days
 *   · stale_clients    — clients with no interaction in > stale_days days
 *   · pending_callbacks — client-initiated "talk to your advisor" requests
 *     from the engagement dashboard / yield simulator (status='pending')
 *
 * Also exposes the per-row action endpoints on POST
 * /api/v1/callback-requests/{id}/{acknowledge,complete,dismiss}.
 */
import authService from './auth.service';

export interface WorkQueueTask {
  task_id: string;
  title: string;
  kind: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  due_date: string | null;
  days_overdue: number;
  client_id: string | null;
  client_name: string | null;
  client_email: string | null;
  commitment_id: string | null;
}

export interface ExpiringCommitment {
  commitment_id: string;
  client_id: string;
  client_name: string;
  client_email: string;
  product_name: string;
  end_date: string;
  days_until_expiry: number;
  cadence: string;
  amount: string;
  currency: string;
}

export interface StaleClient {
  client_id: string;
  client_name: string;
  client_email: string;
  days_since_contact: number | null;
  has_open_followup: boolean;
}

export interface PendingCallback {
  request_id: string;
  client_id: string;
  client_name: string;
  client_email: string;
  /** Renders as a `tel:` link in the Act-now queue row so the agent
   *  can call without leaving the dashboard. May be null for legacy
   *  client rows that pre-date the phone-collection step. */
  client_phone: string | null;
  /** True only while the number we proved is still the one on file (0076). */
  client_phone_verified: boolean;
  product_id: string | null;
  product_name: string | null;
  context_note: string | null;
  created_at: string;
  hours_since_requested: number;
  /** Number of voicemails already left on this row. Surfaces as
   *  "attempt N of 5" in the UI; escalation happens server-side at
   *  the 5th voicemail (see GitLab #64). */
  voicemail_count: number;
}

export interface ExpiredKycClient {
  client_id: string;
  client_name: string;
  client_email: string;
  /** Number of distinct lapsed documents on the client's file. */
  expired_count: number;
  /** Earliest expiry date among the lapsed documents (ISO date). */
  earliest_expiry: string;
}

export interface WorkQueue {
  agent_id: string;
  tasks: WorkQueueTask[];
  expiring_commitments: ExpiringCommitment[];
  stale_clients: StaleClient[];
  pending_callbacks: PendingCallback[];
  /** Clients whose KYC has lapsed — "be aware" bucket. They can still
   *  move money; only new contracts are blocked. The agent's job is to
   *  ask for the re-upload on next contact. */
  expired_kyc: ExpiredKycClient[];
}

export interface CallbackActionResult {
  request_id: string;
  status: 'acknowledged' | 'completed' | 'dismissed';
}

/** Bounds the dismiss route enforces on `reason` (DismissCallbackRequest). */
export const DISMISS_REASON_MIN = 3;
export const DISMISS_REASON_MAX = 500;

/** Why a dismiss reason cannot be sent yet, or null when it can. The server
 *  also refuses markup and control characters and answers with its own reason. */
export function dismissReasonProblem(raw: string): string | null {
  const reason = raw.trim();
  if (reason.length < DISMISS_REASON_MIN) {
    return `Write a reason of at least ${DISMISS_REASON_MIN} characters.`;
  }
  if (reason.length > DISMISS_REASON_MAX) {
    return `Keep the reason under ${DISMISS_REASON_MAX} characters.`;
  }
  return null;
}

class WorkQueueService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  async fetch(
    agentId: string,
    options: { commitmentLookaheadDays?: number; staleDays?: number } = {},
  ): Promise<WorkQueue> {
    const params: Record<string, number> = {};
    if (options.commitmentLookaheadDays !== undefined) {
      params['commitment_lookahead_days'] = options.commitmentLookaheadDays;
    }
    if (options.staleDays !== undefined) {
      params['stale_days'] = options.staleDays;
    }
    const res = await this.api.get<WorkQueue>(
      `/api/v1/agents/${agentId}/work-queue`,
      { params },
    );
    return res.data;
  }

  /** True when priority is high or urgent — drives visual emphasis. */
  isUrgent(task: WorkQueueTask): boolean {
    return task.priority === 'high' || task.priority === 'urgent';
  }

  // ─── Callback request actions ────────────────────────────────────

  async acknowledgeCallback(requestId: string): Promise<CallbackActionResult> {
    const res = await this.api.post<CallbackActionResult>(
      `/api/v1/callback-requests/${requestId}/acknowledge`,
    );
    return res.data;
  }

  async completeCallback(requestId: string): Promise<CallbackActionResult> {
    const res = await this.api.post<CallbackActionResult>(
      `/api/v1/callback-requests/${requestId}/complete`,
    );
    return res.data;
  }

  async dismissCallback(requestId: string, reason: string): Promise<CallbackActionResult> {
    const res = await this.api.post<CallbackActionResult>(
      `/api/v1/callback-requests/${requestId}/dismiss`,
      { reason: reason.trim() },
    );
    return res.data;
  }

  /** Voicemail outcome — the row re-opens at `nextAttemptAt`. If the
   *  agent has already logged 5 prior voicemails the server escalates
   *  instead and returns `status: 'escalated'`. The caller is expected
   *  to have already written the `voicemail_left` interaction via the
   *  staff interactions endpoints; this is the callback-row transition
   *  side only. */
  async recordCallbackVoicemail(
    requestId: string,
    nextAttemptAt: Date,
  ): Promise<CallbackActionResult> {
    const res = await this.api.post<CallbackActionResult>(
      `/api/v1/callback-requests/${requestId}/voicemail`,
      { next_attempt_at: nextAttemptAt.toISOString() },
    );
    return res.data;
  }
}

export default new WorkQueueService();
